import React from "react"
import { DateTime } from "luxon"
import { useFormikContext } from "formik"
import { useQuery } from "@apollo/client/react"
import { type OpeningHour } from "@/utils/opening_hours"
import { GetPublicTableBusyBlocksByDateDocument } from "@/graphql/graphql"
import {
  getSidebarAvailability,
  getSidebarReservationDuration,
  isSidebarDateClosed,
} from "./reservation_sidebar_availability"

type ReservationFormValues = {
  date: DateTime | null
  peopleAmount: number
  startTime: string
  endTime: string
}

type ReservationSidebarStep = "date" | "guests" | "time"

type ReservationBookingFlowProps = {
  venueSlug: string
  timezone: string
  openingHours?: OpeningHour[]
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  tables?: Array<{ id: string; minCapacity?: number | null; maxCapacity?: number | null }>
}

const GUEST_RANGE = { min: 1, max: 12 }

export const useReservationBookingFlow = ({
  venueSlug,
  timezone,
  openingHours,
  minAdvanceBookingTime,
  minReservationTime,
  tables,
}: ReservationBookingFlowProps) => {
  const { values, setFieldValue } = useFormikContext<ReservationFormValues>()
  const [activeStep, setActiveStep] = React.useState<ReservationSidebarStep>("date")
  const [confirmedPeopleAmount, setConfirmedPeopleAmount] = React.useState<number | null>(null)
  const [timeSelectionConfirmed, setTimeSelectionConfirmed] = React.useState(false)
  const hasValidDate = Boolean(values.date?.isValid)
  const selectedDateClosed = values.date ? isSidebarDateClosed(values.date, openingHours, timezone) : false
  const selectedDateIso = values.date?.toISODate() || ""
  const shouldLoadAvailability = Boolean(selectedDateIso && !selectedDateClosed)
  const {
    data: tableBusyBlocksData,
    loading: isAvailabilityLoading,
    error: availabilityError,
    refetch,
  } = useQuery(GetPublicTableBusyBlocksByDateDocument, {
    skip: !shouldLoadAvailability,
    variables: { slug: venueSlug, date: selectedDateIso },
  })

  const availability = React.useMemo(() => {
    if (!values.date || selectedDateClosed) {
      return { isClosed: true, availableGuestCounts: [], timeSlots: [] }
    }

    if (isAvailabilityLoading || availabilityError) {
      return { isClosed: false, availableGuestCounts: [], timeSlots: [] }
    }

    return getSidebarAvailability({
      date: values.date,
      openingHours,
      tables,
      busyBlocks: tableBusyBlocksData?.public?.venue?.tableBusyBlocksByDate || [],
      timezone,
      minAdvanceBookingTime,
      minReservationTime,
      selectedPeopleAmount: values.peopleAmount,
      maxGuestCount: GUEST_RANGE.max,
    })
  }, [
    availabilityError,
    isAvailabilityLoading,
    minAdvanceBookingTime,
    minReservationTime,
    openingHours,
    selectedDateClosed,
    tableBusyBlocksData?.public?.venue?.tableBusyBlocksByDate,
    tables,
    timezone,
    values.date,
    values.peopleAmount,
  ])

  const effectiveConfirmedPeopleAmount = values.startTime ? values.peopleAmount : confirmedPeopleAmount
  const hasValidGuestSelection =
    effectiveConfirmedPeopleAmount !== null &&
    availability.availableGuestCounts.includes(effectiveConfirmedPeopleAmount)

  React.useEffect(() => {
    if (isAvailabilityLoading || availabilityError) return

    if (confirmedPeopleAmount !== null && !availability.availableGuestCounts.includes(confirmedPeopleAmount)) {
      setConfirmedPeopleAmount(null)
      setTimeSelectionConfirmed(false)
      void setFieldValue("startTime", "")
      void setFieldValue("endTime", "")

      if (activeStep === "time") {
        setActiveStep("guests")
      }
    }
  }, [
    activeStep,
    availability.availableGuestCounts,
    availabilityError,
    confirmedPeopleAmount,
    isAvailabilityLoading,
    setFieldValue,
  ])

  React.useEffect(() => {
    if (isAvailabilityLoading || availabilityError) return

    const selectedTimeAvailable = availability.timeSlots.some(
      (slot) => slot.startTime === values.startTime && slot.available,
    )

    if (timeSelectionConfirmed && !selectedTimeAvailable) {
      setTimeSelectionConfirmed(false)
      void setFieldValue("startTime", "")
      void setFieldValue("endTime", "")
    }
  }, [
    availability.timeSlots,
    availabilityError,
    isAvailabilityLoading,
    setFieldValue,
    timeSelectionConfirmed,
    values.startTime,
  ])

  const handleStepClick = (step: ReservationSidebarStep) => {
    if (step === "date") {
      setActiveStep(step)
      return
    }

    if (!hasValidDate) return
    if (step === "time" && !hasValidGuestSelection) return

    setActiveStep(step)
  }

  const handleDateChange = async (date: DateTime, autoAdvance = false) => {
    await setFieldValue("date", date)
    await setFieldValue("startTime", "")
    await setFieldValue("endTime", "")
    setConfirmedPeopleAmount(null)
    setTimeSelectionConfirmed(false)

    if (autoAdvance) {
      setActiveStep("guests")
    }
  }

  const handlePeopleAmountChange = async (peopleAmount: number, autoAdvance = false) => {
    await setFieldValue("peopleAmount", peopleAmount)
    await setFieldValue("startTime", "")
    await setFieldValue("endTime", "")
    setConfirmedPeopleAmount(peopleAmount)
    setTimeSelectionConfirmed(false)

    if (autoAdvance) {
      setActiveStep("time")
    }
  }

  const handleTimeSelect = async (startTime: string) => {
    await setFieldValue("startTime", startTime)
    const startDateTime = DateTime.fromISO(startTime, { setZone: true })
    const duration = getSidebarReservationDuration(minReservationTime)
    await setFieldValue("endTime", startDateTime.plus({ seconds: duration }).toISO())
    setTimeSelectionConfirmed(true)
  }

  return {
    values,
    activeStep,
    setActiveStep,
    hasValidDate,
    hasValidGuestSelection,
    effectiveConfirmedPeopleAmount,
    timeSelectionConfirmed,
    availability,
    isAvailabilityLoading,
    availabilityError,
    refetch,
    handleStepClick,
    handleDateChange,
    handlePeopleAmountChange,
    handleTimeSelect,
  }
}

export type ReservationBookingFlowController = ReturnType<typeof useReservationBookingFlow>
export type { ReservationSidebarStep }
